import axios from "axios";
import React, { Component } from "react";
import cookie from "react-cookies";
import {
  Alert,
  Button,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
} from "reactstrap";
import classnames from "classnames";
import constants from "../../Constants";
import "../integrations/integrations.css";
import "./settings.css";

const baseURL = constants.baseURL;

const SECTIONS = [
  { id: "email", label: "Email address", icon: "mdi-email-edit-outline" },
  { id: "notifications", label: "Notifications", icon: "mdi-bell-outline" },
  { id: "signin", label: "Sign-in & session", icon: "mdi-shield-key-outline" },
];

const RELATED_LINKS = [
  { href: "/profile", icon: "mdi-account-outline", label: "Account profile", desc: "Name, phone, business details" },
  { href: "/profile#finance", icon: "mdi-cash-multiple", label: "Billing & finance", desc: "Settlement mode and invoice email" },
  { href: "/integrations", icon: "mdi-puzzle-outline", label: "Integrations", desc: "Widget domains and Partner API keys" },
  { href: "/support", icon: "mdi-lifebuoy", label: "Support", desc: "Help with trips, billing, and API access" },
];

function sectionFromHash() {
  const hash = (window.location.hash || "").replace(/^#/, "");
  return SECTIONS.some((s) => s.id === hash) ? hash : "email";
}

class Settings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeSection: sectionFromHash(),
      account: null,
      loading: true,
      currentEmail: "",
      newEmail: "",
      otp: "",
      emailChangeStep: 1,
      emailChangeLoading: false,
      emailChangeError: "",
      emailChangeSuccess: "",
      notificationsLoading: false,
      notificationsSaving: false,
      notificationsError: "",
      notificationsSuccess: "",
      notifications: {
        sms_on_booking: false,
        sms_on_admin_assigned: false,
        whatsapp_number: "",
      },
    };
  }

  componentDidMount() {
    document.title = "Settings | QikTruck";
    this.fetchAccount();
    this.fetchNotifications();
    window.addEventListener("hashchange", this.onHashChange);
  }

  componentWillUnmount() {
    window.removeEventListener("hashchange", this.onHashChange);
  }

  onHashChange = () => {
    this.setState({ activeSection: sectionFromHash() });
  };

  go = (id) => {
    window.location.hash = id;
    this.setState({ activeSection: id });
  };

  authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: cookie.load("session-id-qt"),
  });

  fetchAccount = () => {
    const sessionId = cookie.load("session-id-qt");
    if (!sessionId) {
      this.setState({ loading: false });
      return;
    }

    axios
      .get(`${baseURL}/merchant/account-profile`, { headers: this.authHeaders() })
      .then((res) => {
        const account = res.data?.account || null;
        const email = account?.email || "";
        this.setState({
          account,
          currentEmail: email,
          loading: false,
        });
      })
      .catch(() => {
        axios
          .get(`${baseURL}/customer-profile`, { headers: this.authHeaders() })
          .then((res) => {
            const email = res.data?.email || "";
            this.setState({ currentEmail: email, loading: false });
          })
          .catch(() => this.setState({ loading: false }));
      });
  };

  fetchNotifications = () => {
    const sessionId = cookie.load("session-id-qt");
    if (!sessionId) return;

    this.setState({ notificationsLoading: true, notificationsError: "" });
    axios
      .get(`${baseURL}/partner/notifications`, { headers: this.authHeaders() })
      .then((res) => {
        const n = res.data?.notifications || {};
        this.setState({
          notificationsLoading: false,
          notifications: {
            sms_on_booking: n.sms_on_booking === true,
            sms_on_admin_assigned: n.sms_on_admin_assigned === true,
            whatsapp_number: n.whatsapp_number || "",
          },
        });
      })
      .catch((err) => {
        const status = err?.response?.status;
        // 403 = no integrations permission; leave defaults (off)
        this.setState({
          notificationsLoading: false,
          notificationsError:
            status === 403
              ? ""
              : err?.response?.data?.message || "Could not load notification settings.",
        });
      });
  };

  handleSaveNotifications = (e) => {
    if (e) e.preventDefault();
    const { notifications } = this.state;
    this.setState({
      notificationsSaving: true,
      notificationsError: "",
      notificationsSuccess: "",
    });
    axios
      .put(
        `${baseURL}/partner/notifications`,
        {
          notifications: {
            sms_on_booking: !!notifications.sms_on_booking,
            sms_on_admin_assigned: !!notifications.sms_on_admin_assigned,
            whatsapp_number: notifications.whatsapp_number || null,
          },
        },
        { headers: this.authHeaders() }
      )
      .then((res) => {
        const n = res.data?.notifications || {};
        this.setState({
          notificationsSaving: false,
          notificationsSuccess: "Notification settings saved.",
          notifications: {
            sms_on_booking: n.sms_on_booking === true,
            sms_on_admin_assigned: n.sms_on_admin_assigned === true,
            whatsapp_number: n.whatsapp_number || "",
          },
        });
      })
      .catch((err) => {
        this.setState({
          notificationsSaving: false,
          notificationsError:
            err?.response?.data?.message || "Could not save notification settings.",
        });
      });
  };

  handleRequestEmailChange = (e) => {
    if (e) e.preventDefault();
    const { newEmail, currentEmail } = this.state;

    if (!newEmail) {
      this.setState({ emailChangeError: "Please enter a new email address" });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      this.setState({ emailChangeError: "Please enter a valid email address" });
      return;
    }

    if (newEmail.toUpperCase() === (currentEmail || "").toUpperCase()) {
      this.setState({ emailChangeError: "New email must be different from your current email" });
      return;
    }

    this.setState({ emailChangeLoading: true, emailChangeError: "", emailChangeSuccess: "" });

    axios
      .post(`${baseURL}/customer-request-email-change`, { newEmail }, { headers: this.authHeaders() })
      .then((response) => {
        if (response.status === 200) {
          this.setState({
            emailChangeStep: 2,
            emailChangeLoading: false,
            emailChangeSuccess: `Verification code sent to ${newEmail}`,
          });
        }
      })
      .catch((error) => {
        const msg = error.response?.data?.error || "Failed to send verification code. Please try again.";
        this.setState({ emailChangeLoading: false, emailChangeError: msg });
      });
  };

  handleVerifyEmailChange = (e) => {
    e.preventDefault();
    const { newEmail, otp } = this.state;

    if (!otp || otp.trim().length < 6) {
      this.setState({ emailChangeError: "Please enter the 6-character verification code" });
      return;
    }

    this.setState({ emailChangeLoading: true, emailChangeError: "", emailChangeSuccess: "" });

    axios
      .post(
        `${baseURL}/customer-verify-email-change`,
        { newEmail, otp: otp.trim().toUpperCase() },
        { headers: this.authHeaders() }
      )
      .then((response) => {
        if (response.status === 200) {
          const updated = newEmail.toUpperCase();
          this.setState({
            emailChangeStep: 3,
            emailChangeLoading: false,
            emailChangeSuccess: "Email updated successfully.",
            currentEmail: updated,
            account: this.state.account ? { ...this.state.account, email: updated } : null,
          });
        }
      })
      .catch((error) => {
        const msg = error.response?.data?.error || "Verification failed. Please try again.";
        this.setState({ emailChangeLoading: false, emailChangeError: msg });
      });
  };

  resetEmailChange = () => {
    this.setState({
      newEmail: "",
      otp: "",
      emailChangeStep: 1,
      emailChangeError: "",
      emailChangeSuccess: "",
      emailChangeLoading: false,
    });
  };

  renderEmailSteps() {
    const step = this.state.emailChangeStep;
    const steps = [
      { n: 1, label: "New email" },
      { n: 2, label: "Verify code" },
      { n: 3, label: "Done" },
    ];
    return (
      <div className="qt-step-row" aria-label="Email change progress">
        {steps.map((s) => (
          <div
            key={s.n}
            className={classnames("qt-step", {
              "is-active": step === s.n,
              "is-done": step > s.n,
            })}
          >
            <span className="qt-step-num">{step > s.n ? <i className="mdi mdi-check" /> : s.n}</span>
            <span className="qt-step-label">{s.label}</span>
          </div>
        ))}
      </div>
    );
  }

  renderEmailSection() {
    const {
      currentEmail,
      newEmail,
      otp,
      emailChangeStep,
      emailChangeLoading,
      emailChangeError,
      emailChangeSuccess,
    } = this.state;

    return (
      <div className="qt-card">
        <div className="qt-card-body">
          <div className="qt-section-head">
            <div className="qt-section-icon qt-section-icon--orange">
              <i className="mdi mdi-email-edit-outline" />
            </div>
            <div>
              <h4 className="qt-section-title">Change email address</h4>
              <p className="qt-section-desc mb-0">
                This is the email you use to sign in and receive trip and billing notices. We&apos;ll send a
                one-time code to your new address before switching.
              </p>
            </div>
          </div>

          <div className="qt-email-chip">
            <i className="mdi mdi-email-outline" />
            Current: <code>{currentEmail || "-"}</code>
          </div>

          {this.renderEmailSteps()}

          {emailChangeError ? (
            <Alert color="danger" className="qt-alert mb-3">
              <i className="mdi mdi-alert-circle-outline me-2" />
              {emailChangeError}
            </Alert>
          ) : null}
          {emailChangeSuccess && emailChangeStep !== 3 ? (
            <Alert color="success" className="qt-alert mb-3">
              <i className="mdi mdi-check-circle-outline me-2" />
              {emailChangeSuccess}
            </Alert>
          ) : null}

          {emailChangeStep === 1 ? (
            <Form onSubmit={this.handleRequestEmailChange}>
              <FormGroup>
                <Label for="newEmail">New email address</Label>
                <Input
                  id="newEmail"
                  type="email"
                  placeholder="you@company.co.za"
                  value={newEmail}
                  onChange={(e) => this.setState({ newEmail: e.target.value, emailChangeError: "" })}
                  autoComplete="email"
                  required
                />
                <small className="text-muted">
                  Invoice PDFs and login codes will move to this address once verified.
                </small>
              </FormGroup>
              <Button color="primary" type="submit" disabled={emailChangeLoading}>
                {emailChangeLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Sending code…
                  </>
                ) : (
                  <>
                    <i className="mdi mdi-email-fast-outline me-1" />
                    Send verification code
                  </>
                )}
              </Button>
            </Form>
          ) : null}

          {emailChangeStep === 2 ? (
            <Form onSubmit={this.handleVerifyEmailChange}>
              <FormGroup>
                <Label for="otp">Verification code</Label>
                <Input
                  id="otp"
                  className="qt-otp-input"
                  type="text"
                  inputMode="text"
                  autoComplete="one-time-code"
                  placeholder="······"
                  value={otp}
                  onChange={(e) =>
                    this.setState({
                      otp: e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 6).toUpperCase(),
                      emailChangeError: "",
                    })
                  }
                  maxLength={6}
                  required
                />
                <small className="text-muted d-block mt-2">
                  Enter the 6-character code sent to <strong>{newEmail}</strong>. Codes expire after a short
                  time.
                </small>
              </FormGroup>
              <div className="d-flex flex-wrap gap-2">
                <Button color="primary" type="submit" disabled={emailChangeLoading}>
                  {emailChangeLoading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Verifying…
                    </>
                  ) : (
                    <>
                      <i className="mdi mdi-check-decagram me-1" />
                      Verify &amp; update email
                    </>
                  )}
                </Button>
                <Button
                  color="outline-secondary"
                  type="button"
                  disabled={emailChangeLoading}
                  onClick={() => this.handleRequestEmailChange()}
                >
                  Resend code
                </Button>
                <Button color="link" type="button" className="text-muted" onClick={this.resetEmailChange}>
                  Use a different email
                </Button>
              </div>
            </Form>
          ) : null}

          {emailChangeStep === 3 ? (
            <div className="qt-success-panel">
              <i className="mdi mdi-check-circle-outline d-block" />
              <h5 className="mb-2">Email updated</h5>
              <p className="text-muted mb-3">
                Your sign-in email is now <strong>{currentEmail}</strong>. Use it the next time you log in.
              </p>
              <Button color="outline-primary" size="sm" onClick={this.resetEmailChange}>
                Change again
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  renderSignInSection() {
    const { account, currentEmail } = this.state;
    const name = [account?.firstname, account?.lastname].filter(Boolean).join(" ");

    return (
      <>
        <div className="qt-card mb-3">
          <div className="qt-card-body">
            <div className="qt-section-head">
              <div className="qt-section-icon qt-section-icon--teal">
                <i className="mdi mdi-shield-key-outline" />
              </div>
              <div>
                <h4 className="qt-section-title">How you sign in</h4>
                <p className="qt-section-desc mb-0">
                  The business portal uses passwordless sign-in. Enter your email on the login page and we send a
                  one-time code - no password to remember or reset.
                </p>
              </div>
            </div>

            <div className="qt-signin-card">
              <div className="qt-signin-card-icon">
                <i className="mdi mdi-email-lock-outline" />
              </div>
              <div>
                <h6>Email + one-time code</h6>
                <p>
                  {name ? (
                    <>
                      Signed in as <strong>{name}</strong>
                      {currentEmail ? (
                        <>
                          {" "}
                          (<code>{currentEmail}</code>)
                        </>
                      ) : null}
                      . Each new browser or device needs a fresh code from your inbox.
                    </>
                  ) : (
                    <>
                      Your account email receives login codes. Each new browser or device needs a fresh code from
                      your inbox.
                    </>
                  )}
                </p>
              </div>
            </div>

            <div className="qt-signin-card">
              <div className="qt-signin-card-icon">
                <i className="mdi mdi-laptop" />
              </div>
              <div>
                <h6>Stay signed in on trusted devices</h6>
                <p>
                  Sessions last until you log out or clear cookies. Use a private window on shared computers, then
                  sign out when finished.
                </p>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2 mt-2">
              <a href="/logout" className="btn btn-outline-secondary btn-sm">
                <i className="mdi mdi-logout me-1" />
                Log out on this device
              </a>
            </div>
          </div>
        </div>

        <div className="qt-settings-note">
          <i className="mdi mdi-information-outline" />
          <div>
            <strong>Need to update your name or phone?</strong> Those live under{" "}
            <a href="/profile">Account profile</a>. Finance and integration contacts are managed there too.
          </div>
        </div>
      </>
    );
  }

  renderSidebar() {
    return (
      <div className="qt-sidebar">
        <div className="qt-sidebar-card mb-3">
          <div className="qt-sidebar-head">
            <i className="mdi mdi-cog-outline me-1" />
            Settings
          </div>
          <Nav vertical className="qt-side-nav">
            {SECTIONS.map((sec) => (
              <NavItem key={sec.id}>
                <NavLink
                  className={classnames({ active: this.state.activeSection === sec.id })}
                  onClick={() => this.go(sec.id)}
                  style={{ cursor: "pointer" }}
                >
                  <i className={"mdi " + sec.icon} />
                  {sec.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </div>

        <div className="qt-sidebar-card">
          <div className="qt-sidebar-head">
            <i className="mdi mdi-link-variant me-1" />
            Related
          </div>
          <div className="p-3">
            <div className="qt-link-list">
              {RELATED_LINKS.map((link) => (
                <a key={link.href} href={link.href}>
                  <i className={"mdi " + link.icon} />
                  <span>
                    <strong className="d-block" style={{ fontSize: "0.86rem" }}>
                      {link.label}
                    </strong>
                    <span className="text-muted" style={{ fontSize: "0.76rem" }}>
                      {link.desc}
                    </span>
                  </span>
                  <i className="mdi mdi-chevron-right ms-auto text-muted" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderNotificationsSection() {
    const {
      notifications,
      notificationsLoading,
      notificationsSaving,
      notificationsError,
      notificationsSuccess,
    } = this.state;

    return (
      <div className="qt-card">
        <div className="qt-card-body">
          <div className="qt-section-head">
            <div className="qt-section-icon qt-section-icon--teal">
              <i className="mdi mdi-bell-outline" />
            </div>
            <div>
              <h4 className="qt-section-title">End-customer SMS</h4>
              <p className="qt-section-desc mb-0">
                Control whether QikTruck texts the mobile number on each trip. Off by default for
                business accounts so partners like Battermaster stay quiet until you opt in.
              </p>
            </div>
          </div>

          {notificationsLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" size="sm" />
              <p className="text-muted mt-2 mb-0">Loading notification settings…</p>
            </div>
          ) : (
            <Form onSubmit={this.handleSaveNotifications}>
              {notificationsError ? (
                <Alert color="danger" className="qt-alert mb-3">
                  <i className="mdi mdi-alert-circle-outline me-2" />
                  {notificationsError}
                </Alert>
              ) : null}
              {notificationsSuccess ? (
                <Alert color="success" className="qt-alert mb-3">
                  <i className="mdi mdi-check-circle-outline me-2" />
                  {notificationsSuccess}
                </Alert>
              ) : null}

              <FormGroup check className="mb-3">
                <Input
                  id="sms_on_booking"
                  type="checkbox"
                  checked={!!notifications.sms_on_booking}
                  onChange={(e) =>
                    this.setState({
                      notifications: {
                        ...notifications,
                        sms_on_booking: e.target.checked,
                      },
                      notificationsSuccess: "",
                    })
                  }
                />
                <Label check for="sms_on_booking">
                  SMS when a trip is booked
                </Label>
                <small className="d-block text-muted ms-4">
                  Sends a booking confirmation with a WhatsApp help link to the order mobile number.
                </small>
              </FormGroup>

              <FormGroup check className="mb-3">
                <Input
                  id="sms_on_admin_assigned"
                  type="checkbox"
                  checked={!!notifications.sms_on_admin_assigned}
                  onChange={(e) =>
                    this.setState({
                      notifications: {
                        ...notifications,
                        sms_on_admin_assigned: e.target.checked,
                      },
                      notificationsSuccess: "",
                    })
                  }
                />
                <Label check for="sms_on_admin_assigned">
                  SMS when a coordinator is assigned
                </Label>
                <small className="d-block text-muted ms-4">
                  Includes a WhatsApp link to the assigned coordinator when available.
                </small>
              </FormGroup>

              <FormGroup className="mb-3">
                <Label for="whatsapp_number">Custom WhatsApp number (optional)</Label>
                <Input
                  id="whatsapp_number"
                  type="tel"
                  placeholder="+27 66 378 4460"
                  value={notifications.whatsapp_number || ""}
                  onChange={(e) =>
                    this.setState({
                      notifications: {
                        ...notifications,
                        whatsapp_number: e.target.value,
                      },
                      notificationsSuccess: "",
                    })
                  }
                />
                <small className="text-muted">
                  Leave blank to use QikTruck&apos;s main WhatsApp (or the coordinator&apos;s number on
                  assignment).
                </small>
              </FormGroup>

              <Button color="primary" type="submit" disabled={notificationsSaving}>
                {notificationsSaving ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Saving…
                  </>
                ) : (
                  <>
                    <i className="mdi mdi-content-save-outline me-1" />
                    Save notification settings
                  </>
                )}
              </Button>
            </Form>
          )}
        </div>
      </div>
    );
  }

  renderSectionBody() {
    if (this.state.activeSection === "signin") return this.renderSignInSection();
    if (this.state.activeSection === "notifications") return this.renderNotificationsSection();
    return this.renderEmailSection();
  }

  render() {
    const { loading, account, activeSection } = this.state;

    return (
      <div className="qt-settings qt-account qt-integrations">
        <Container>
          <div className="qt-page-head">
            <div className="qt-breadcrumb">
              <a href="/dashboard">Dashboard</a>
              <span className="mx-1">/</span>
              Settings
            </div>
            <div className="qt-page-title-row">
              <div>
                <h1 className="qt-page-title">
                  <i className="mdi mdi-cog-outline" />
                  Settings
                </h1>
                <p className="qt-page-lead mb-0">
                  Manage your sign-in email, end-customer SMS notifications, and session. Business
                  details, billing, and integrations are in their own sections.
                </p>
              </div>
              {account?.customerid ? (
                <div className="qt-site-chip">
                  <i className="mdi mdi-identifier" />
                  Customer ID <code>{account.customerid}</code>
                </div>
              ) : null}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="text-muted mt-3">Loading settings…</p>
            </div>
          ) : (
            <Row>
              <Col lg={3} className="mb-3 d-none d-lg-block">
                {this.renderSidebar()}
              </Col>
              <Col lg={9}>
                <div className="qt-mobile-nav d-lg-none mb-3">
                  {SECTIONS.map((sec) => (
                    <button
                      key={sec.id}
                      type="button"
                      className={"qt-mobile-nav-btn" + (activeSection === sec.id ? " is-active" : "")}
                      onClick={() => this.go(sec.id)}
                    >
                      <i className={"mdi " + sec.icon} />
                      {sec.label}
                    </button>
                  ))}
                </div>
                <div className="qt-section-panel">{this.renderSectionBody()}</div>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    );
  }
}

export default Settings;
